import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    colors,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Typography
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/EmailOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import PersonIcon from '@material-ui/icons/PersonOutline';
import ScreenLockPortraitIcon from '@material-ui/icons/ScreenLockPortrait';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import useReactRouter from 'use-react-router';
import {Can} from '../../../components/Can';
import Label from '../../../components/Label';
import {formatCellphone} from '../../../components/PhoneInput/PhoneInput';
import {Role, User} from '../../../graphql/auth';
import {EST, formatDateTime} from '../../../utils/formatters';
import forgotPassword from '../../LogIn/ForgotPassword/forgotPassword.api';
import forgotPasswordSms from '../../LogIn/ForgotPassword/forgotPasswordSms.api';
import UserEdit from '../UserEdit';
import impersonateUser from './impersonateUser.api';
import sendVerifyEmail from '../sendVerifyEmail.api';
import UsersSendMessage from '../UsersSendMessageModal';

const useStyles = makeStyles((theme: Theme) => ({
    root: {},
    content: {
        padding: 0
    },
    titleCell: {
        width: '40%',
    },
    dataCell: {
        width: '60%',
    },
    userDisabled: {
        color: theme.palette.error.main,
    },
    actions: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        '& > * + *': {
            marginLeft: 0
        }
    },
    buttonIcon: {
        marginRight: theme.spacing(1)
    }
}));

const UserInfo = (props: { className?: string, user: User }) => {
    const {user, className, ...rest} = props;
    const [sendForgotPassword] = forgotPassword();
    const [sendForgotPasswordSms] = forgotPasswordSms();
    const [sendSendVerifyEmail] = sendVerifyEmail();
    const [sendImpersonateUser] = impersonateUser();
    const {history} = useReactRouter();
    const {enqueueSnackbar} = useSnackbar();

    const classes = useStyles();

    const [openEdit, setOpenEdit] = useState(false);
    const [message, setMessage] = useState('');

    const handleEditOpen = () => {
        setOpenEdit(true);
    };

    const handleEditClose = () => {
        setOpenEdit(false);
    };

    const handlePasswordResetEmail = () => {
        sendForgotPassword({variables: {data: {email: user.email}}})
            .then((response) => {
                setMessage(response.data!.forgotPassword!.status);
                enqueueSnackbar(response.data!.forgotPassword!.status, {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Server communication error', {variant: 'error'});
            });
    };

    const handlePasswordResetSms = () => {
        sendForgotPasswordSms({variables: {data: {cellphone: user.cellphone!, cellphone_code: user.cellphone_code!}}})
            .then((response) => {
                setMessage(response.data!.forgotPasswordSms!.status);
                enqueueSnackbar(response.data!.forgotPasswordSms!.status, {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Server communication error', {variant: 'error'});
            });
    };

    const handleSendVerifyEmail = () => {
        sendSendVerifyEmail({variables: {id: user.id}})
            .then((response) => {
                setMessage(response.data!.sendVerifyEmail!.message);
                enqueueSnackbar(response.data!.sendVerifyEmail!.message, {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Server communication error', {variant: 'error'});
            });
    };

    const handleImpersonateUser = () => {
        sendImpersonateUser({variables: {id: user.id}})
            .then(() => {
                enqueueSnackbar('Impersonate User success.', {variant: 'success'});
                history.push('/');
            })
            .catch(() => {
                enqueueSnackbar('Server communication error', {variant: 'error'});
            });
    };

    const [openMessage, setOpenMessage] = useState(false);
    const showSendMessage = () => setOpenMessage(true);
    const handleMessageClose = () => setOpenMessage(false);


    return (
        <Card{...rest} className={clsx(classes.root, className)}>
            <CardHeader title="User info"/>
            <Divider/>
            <CardContent className={classes.content}>
                <Table>
                    <TableBody>
                        {user.disabled && (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <Typography className={classes.userDisabled} variant="h2">This user is
                                        disabled.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {message !== '' && (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <Typography color="primary" variant="h2">{message}</Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        <TableRow>
                            <TableCell className={classes.titleCell}>Name</TableCell>
                            <TableCell className={classes.dataCell}>{user.first_name} {user.last_name}</TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell className={classes.titleCell}>Admin Verified?</TableCell>
                            <TableCell className={classes.dataCell}>{user.admin_verified ? 'yes' : 'no'}</TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>
                                {user.email}
                                <div>
                                    <Label
                                        color={
                                            user.email_verified ? colors.green[600] : colors.orange[600]
                                        }
                                    >
                                        {user.email_verified ? 'Email verified' : 'Email not verified'}
                                    </Label>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell className={classes.titleCell}>Cellphone</TableCell>
                            <TableCell
                                className={classes.dataCell}>{user.cellphone ? user.cellphone_code + ' ' + formatCellphone(user.cellphone) : ''}</TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell className={classes.titleCell}>Created</TableCell>
                            <TableCell className={classes.dataCell}>{formatDateTime(user.created_at)}</TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell>Roles</TableCell>
                            <TableCell>
                                {user.roles!.map((role: Role) => (<Label key={role.id} variant="outlined"
                                                                         color={colors.green[600]}>{role.name}</Label>))}
                            </TableCell>
                        </TableRow>

                        <TableRow>
                            <TableCell className={classes.titleCell}>Comms opt out?</TableCell>
                            <TableCell className={classes.dataCell}>
                                {user.email_opt_out &&
                                <Label variant="outlined" color={colors.orange[600]}>no email</Label>}
                                {user.sms_opt_out &&
                                <Label variant="outlined" color={colors.orange[600]}>no sms</Label>}
                            </TableCell>
                        </TableRow>

                    </TableBody>
                </Table>
            </CardContent>
            <CardActions className={classes.actions}>
                <Can permission="user.update">
                    <Button onClick={handleEditOpen}>
                        <EditIcon className={classes.buttonIcon}/>
                        Edit
                    </Button>
                </Can>
                <Button onClick={handlePasswordResetEmail}>
                    <LockOutlinedIcon className={classes.buttonIcon}/>
                    Send Password Reset Email
                </Button>
                {user.cellphone && (
                    <Button onClick={handlePasswordResetSms}>
                        <ScreenLockPortraitIcon className={classes.buttonIcon}/>
                        Send Password Reset SMS
                    </Button>
                )}
                {!user.email_verified && (
                    <Button onClick={handleSendVerifyEmail}>
                        <EmailIcon className={classes.buttonIcon}/>
                        Send Verify Email
                    </Button>
                )}
                <Can permission="user.impersonate">
                    <Button onClick={handleImpersonateUser}>
                        <PersonIcon className={classes.buttonIcon}/>
                        Login as User
                    </Button>
                </Can>
                <Button onClick={showSendMessage}>
                    <EmailIcon className={classes.buttonIcon}/>
                    Send Message
                </Button>
            </CardActions>

            {openEdit && (<UserEdit user={user} onClose={handleEditClose} open={openEdit}/>)}

            {openMessage && (<UsersSendMessage open={openMessage} userIds={[user.id]} onClose={handleMessageClose}/>)}
        </Card>
    );
};

export default UserInfo;
